import { AutogenLayoutSuggestedPricing } from "./autogen/AutogenLayoutSuggestedPricing";
import { DateRange, DisplayType, DisplayValue, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { ClickEvent, Snackbar } from "@mcleod/components";
import { RATE_VENDORS } from "./DistanceRateIndexCalculator";

export interface SuggestedPricingProps {
    stopData: ModelRow[];
    billDistance: number;
    equipmentTypeId: string;
    orderId: string;
    commitmentId: string;
    origDestRateId: string;
}

const log = LogManager.getLogger("lme.dispatch.SuggestedPricing");

export class SuggestedPricing extends AutogenLayoutSuggestedPricing {
    private props: SuggestedPricingProps;
    private isLaneHistoryDataLoaded: boolean = false;
    private isRateIndexDataLoaded: boolean = false;
    private isCommitmentDataLoaded: boolean = false;
    override onLoad(): void | Promise<void> {
        this.sourceRateIndexControl.search().then((response) => {
            const data = response["modelRows"];
            const firstSetupVendor = data.find(item => item?.data?.licensed_and_setup);
            this.tabRateIndex.visible = !!firstSetupVendor;
            if(this.tabRateIndex.visible) {
                this.layoutRateIndex.buttonFetchOtherResults.visible = true;
            }
        });

        this.tabsetSuggestedPricing.addBeforeTabSelectionListener(async (event) => {
            if (!this.isLaneHistoryDataLoaded && event.newSelection.element.id === "tabLaneHistory")
                this.populateLaneHistoryInformation();
            else if (!this.isRateIndexDataLoaded && event.newSelection.element.id === "tabRateIndex") {
                const rateRetrievalVendorList = await this.setAutoRetrievalVendorList();
                if (rateRetrievalVendorList.length > 0) {
                    await this.populateRateInformation(rateRetrievalVendorList);
                } else {
                    this.setLayoutRateIndexLastSearchInfo();
                }
            } else if (!this.isCommitmentDataLoaded && event.newSelection.element.id === "tabCommitmentAutoRating")
                await this.populateCommitmentInformation();
        });
    }

    async initialize(props: SuggestedPricingProps): Promise<boolean> {
        this.isLaneHistoryDataLoaded = this.isRateIndexDataLoaded = this.isCommitmentDataLoaded = false;
        this.props = props;
        const validStops = this.props.stopData.length > 1 && this.props.stopData.every((stop) => !stop.isNull("city_id"));
        if (!validStops) {
            Snackbar.showWarningSnackbar("At least two stops are required to populate suggested pricing");
            return false;
        }

        if (this.tabRateIndex.selected) {
            const rateRetrievalVendorList = await this.setAutoRetrievalVendorList();
            if (rateRetrievalVendorList.length > 0) {
                this.populateRateInformation(rateRetrievalVendorList);
            } else {
                this.setLayoutRateIndexLastSearchInfo();
            }
        } else {
            this.tabRateIndex.select();
        }
        return true;
    }

    populateLaneHistoryInformation() {
        this.buttonSearch.clicked();
    }

    populateRateIndexInformation() {
        this.layoutRateIndex.setDefaultValues();
        this.layoutRateIndex.hideColumns();
        this.layoutRateIndex.buttonFetchOtherResults.visible = true;
        this.layoutRateIndex.marginBottom = 20;
        this.layoutRateIndex.search({
            stops: this.props.stopData,
            mileage: this.props.billDistance,
            equipment_id: this.props.equipmentTypeId,
            vendor: RATE_VENDORS.MPACT
        }).then((response) => {
            if (response) {
                const data = response["modelRows"][0].data;
                if (data["vendors_data"].length === 0) {
                    Snackbar.showSnackbar("No rate index results found");
                    return;
                }
                for (const vendorData of data["vendors_data"]) {
                    this.layoutRateIndex.populateRateIndexResultData(vendorData, false, data["show_company_scac"], null)
                }
            } else {
                Snackbar.showSnackbar("Error fetching rate index results");
                log.error("Error fetching rate index results");
            }
        });
        this.isRateIndexDataLoaded = true;
    }

    private async populateRateInformation(rateRetrievalVendorList: any[]) {
        const rateIndexSnackbar = Snackbar.showDownloadSnackbar("Loading...", "Loading rate index information...");
        this.layoutRateIndex.setDefaultValues();
        this.layoutRateIndex.hideColumns();
        this.layoutRateIndex.buttonFetchOtherResults.visible = true;
        this.layoutRateIndex.marginBottom = 20;

        try {
            // Process each vendor sequentially to maintain order
            for (const vendor of rateRetrievalVendorList) {
                try {
                    const searchParams = {
                        stops: this.props.stopData,
                        mileage: this.props.billDistance,
                        equipment_id: this.props.equipmentTypeId,
                        vendor: vendor,
                        show_all_total_rates: true
                    };

                    const response = await this.layoutRateIndex.search(searchParams);
                    if (response?.modelRows?.[0]?.data) {
                        const data = response.modelRows[0].data;
                        if (data.vendors_data?.length > 0) {
                            data.vendors_data.forEach(vendorData => {
                                this.layoutRateIndex.populateRateIndexResultData(
                                    vendorData,
                                    false,
                                    data.show_company_scac,
                                    null
                                );
                            });
                        } else {
                            Snackbar.showSnackbar(`No rate index results for vendor: ${this.getVendorName(vendor)}`);
                        }
                    } else {
                         Snackbar.showSnackbar(`Error fetching rate index results for vendor: ${this.getVendorName(vendor)}`);
                    }
                } catch (error) {
                     Snackbar.showSnackbar(`Error processing vendor: ${this.getVendorName(vendor)}`);
                }
            }
        } catch (error) {
            Snackbar.showSnackbar("Unexpected error during rate retrieval.");
        } finally {
            rateIndexSnackbar.dismiss(0, true);
            this.isRateIndexDataLoaded = true;
        }
    }


    async populateCommitmentInformation() {
        const filter = this.props.commitmentId ? {
            id: this.props.origDestRateId,
            "commitment_detail.id": this.props.commitmentId
        } : { id: this.props.origDestRateId };
        if (StringUtil.isEmptyString(this.props.commitmentId)) {
            Snackbar.showSnackbar("No active commitment detail found. Order did not receive a commitment ID when auto rated.");
        } else if (StringUtil.isEmptyString(this.props.origDestRateId)) {
            Snackbar.showSnackbar("No origin destination rate ID found.");
        } else {
            this.layoutCommitmentAutoRating.search(filter);
        }
        this.isCommitmentDataLoaded = true;
    }

    buttonSearchOnClick(event: ClickEvent) {
        const originStop = this.props.stopData[0];
        const destinationStop = this.props.stopData[this.props.stopData.length - 1];
        const findNearCityId = originStop.get("city_id");
        const destinationState = destinationStop.get("state");
        const filter: ModelRow = this.sourceLaneHistory.getSearchFilter();
        if (filter.isNull("stop_origin.sched_arrive_early")) {
            const dateRange = DateRange.parseDateRangeWithKeywords("t-180,t");
            this.textboxDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
        }
        if (filter.isNull("find_near_radius")) {
            this.textboxRadius.text = "75";
            filter.set("find_near_radius", 75)
        }
        filter.setValues({
            find_near_city_id: findNearCityId,
            destination_state: destinationState
        });
        this.citystateStopOriginCityId.displayComponentData(originStop, null, 0);
        this.textboxDestinationState.displayComponentData(destinationStop, null, 0);
        this.sourceLaneHistory.search(filter.data).finally(() => {
            this.isLaneHistoryDataLoaded = true;
        });
    }

    private async setAutoRetrievalVendorList() {
        const rateRetrievalVendorList = [];
        await this.sourceRateIndexControl.search().then(data => {
            if (data && data.modelRows) {
                data.modelRows.forEach(row => {
                    if (!StringUtil.isEmptyString(row?._data?.auto_rate_retrieval) && StringUtil.equalsIgnoreCase(row?._data?.auto_rate_retrieval, "Y")) {
                        rateRetrievalVendorList.push(row?._data.vendor);
                    }
                })
            }
        });
        return rateRetrievalVendorList;
    }

    private getVendorName(vendor: string): string {
        switch (vendor.toUpperCase()) {
            case "T":
                return "DAT";
            case "I":
                return "Internet Truckstop";
            case "E":
                return "McLeod Exchange";
            case "F":
                return "SONAR TRAC";
            case "G":
                return "Greenscreens";
            default:
                return "";
        }
    }

    private setLayoutRateIndexLastSearchInfo() {
        this.layoutRateIndex.mainDataSource.lastSearch = {
            stops: this.props.stopData,
            mileage: this.props.billDistance,
            equipment_id: this.props.equipmentTypeId,
            vendor: RATE_VENDORS.MPACT
        }
    }
}
